import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiBellOff,
  FiGlobe,
  FiHeadphones,
  FiLock,
  FiMessageCircle,
  FiMic,
  FiShield,
  FiUserPlus,
  FiVideo,
} from 'react-icons/fi';

const navItems = [
  { to: '/features', label: 'Features' },
  { to: '/how-it-works', label: 'How it works' },
  { to: '/silent-messaging', label: 'Silent messaging' },
  { to: '/browser-video-calling', label: 'Video calling' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/about', label: 'About' },
  { to: '/blog', label: 'Blog' },
];

const reasons = [
  {
    icon: FiBellOff,
    title: 'No notification pressure',
    text: 'Open Whizpr when you want to talk, without turning every message into a demand for attention.',
  },
  {
    icon: FiGlobe,
    title: 'Browser-based',
    text: 'Use Whizpr from the web and invite people without asking them to install a separate app first.',
  },
  {
    icon: FiMessageCircle,
    title: 'Text, voice and calls',
    text: 'Keep text chats, voice messages, audio calls, and video calls together in one private conversation space.',
  },
];

const capabilities = [
  { icon: FiMessageCircle, label: 'Text chats' },
  { icon: FiMic, label: 'Voice messages' },
  { icon: FiHeadphones, label: 'Audio calls' },
  { icon: FiVideo, label: 'Video calls' },
  { icon: FiUserPlus, label: 'Private invites' },
  { icon: FiShield, label: 'Privacy-focused' },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Whizpr | Private conversations without the noise';
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      'content',
      'Whizpr is a privacy-focused silent messaging web app for text, voice messages, audio calls, and video calls without notification pressure.'
    );
  }, []);

  return (
    <main className="min-h-screen bg-[#02030B] text-white">
      <section className="relative overflow-hidden px-5 pb-12 pt-4">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_78%_12%,rgba(126,63,242,0.42),transparent_30%),radial-gradient(circle_at_8%_38%,rgba(117,55,213,0.32),transparent_34%),radial-gradient(circle_at_86%_54%,rgba(58,125,255,0.24),transparent_26%),linear-gradient(180deg,#02030B_0%,#0A0820_48%,#02030B_100%)]" />
        <div className="pointer-events-none absolute left-[-110px] top-[260px] h-72 w-72 rounded-full border border-[#7C3AED]/30" />
        <div className="pointer-events-none absolute right-[-92px] top-[420px] h-56 w-56 rounded-full border border-[#3B82F6]/30" />

        <div className="relative mx-auto flex min-h-[92vh] w-full max-w-6xl flex-col">
          <header className="flex flex-col gap-4 py-2 sm:flex-row sm:items-center sm:justify-between">
            <Link to="/" className="inline-flex items-center gap-3">
              <img src="/whizpr-logo.png" alt="Whizpr logo" className="h-11 w-11 rounded-xl object-cover shadow-[0_0_28px_rgba(124,58,237,0.72)]" />
              <span className="font-brand text-2xl font-extrabold tracking-normal">whizpr</span>
            </Link>
            <nav className="flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold text-[#D9D7FF]">
              {navItems.map((item) => (
                <Link key={item.to} to={item.to} className="transition hover:text-[#8B5CF6]">
                  {item.label}
                </Link>
              ))}
            </nav>
          </header>

          <div className="grid flex-1 gap-10 py-12 lg:grid-cols-[1.02fr_0.98fr] lg:items-center lg:py-16">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-[#8B5CF6]/70 bg-[#07050F]/70 px-4 py-2 text-xs font-extrabold uppercase tracking-normal text-white shadow-[0_0_30px_rgba(124,58,237,0.35)]">
                <FiBellOff className="h-4 w-4" />
                Silent messaging for private conversations
              </div>
              <h1 className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-normal text-white sm:text-6xl">
                Private conversations without the noise.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-8 text-[#D9D7FF] sm:text-xl sm:leading-9">
                No distracting notifications. No public profile. Just text, voice, and video with the people you choose.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center justify-center gap-3 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] px-6 py-4 text-base font-extrabold text-white shadow-[0_24px_52px_rgba(59,130,246,0.28)] transition hover:brightness-110 focus:outline-none focus:ring-4 focus:ring-[#8B5CF6]/30"
                >
                  <FiMessageCircle className="h-6 w-6" />
                  Open Whizpr
                </button>
                <Link
                  to="/features"
                  className="inline-flex items-center justify-center gap-3 rounded-lg border border-[#8B5CF6]/45 px-6 py-4 text-base font-extrabold text-white transition hover:bg-[#7C3AED]/15"
                >
                  <FiShield className="h-6 w-6" />
                  Explore features
                </Link>
              </div>

              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm font-semibold text-[#B9B5D7]">
                <Link to="/privacy-policy" className="underline decoration-[#8B5CF6] underline-offset-4">
                  Privacy Policy
                </Link>
                <Link to="/terms-of-service" className="underline decoration-[#3B82F6] underline-offset-4">
                  Terms of Service
                </Link>
              </div>
            </div>

            <div className="rounded-lg border border-[#7C3AED]/35 bg-[#07050F]/75 p-4 shadow-2xl shadow-[#1E0A50]/35 backdrop-blur">
              <div className="rounded-lg border border-[#7C3AED]/25 bg-[#02030B] p-4">
                <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#7C3AED]/20 text-[#A78BFA]">
                      <FiLock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-white">Chosen conversation</p>
                      <p className="text-xs font-semibold text-[#B9B5D7]">Quiet private space</p>
                    </div>
                  </div>
                  <span className="rounded-full bg-[#3B82F6]/15 px-3 py-1 text-xs font-extrabold text-[#93C5FD]">
                    Online
                  </span>
                </div>

                <div className="space-y-4 py-5">
                  <div className="max-w-[82%] rounded-lg rounded-tl-sm bg-white/[0.06] p-4">
                    <p className="text-sm leading-6 text-[#E4E4E7]">
                      Let us talk here. It feels calmer than the noisy apps.
                    </p>
                  </div>
                  <div className="ml-auto max-w-[82%] rounded-lg rounded-tr-sm bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] p-4 text-white">
                    <p className="text-sm font-semibold leading-6">
                      Yes. Text, voice note, or call whenever we actually want to connect.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] p-4">
                    <FiMic className="h-6 w-6 text-[#22D3EE]" />
                    <div className="h-2 flex-1 rounded-full bg-white/10">
                      <div className="h-2 w-2/3 rounded-full bg-[#3B82F6]" />
                    </div>
                    <span className="text-xs font-bold text-[#B9B5D7]">0:18</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
                  <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/[0.06] px-3 py-3 text-sm font-extrabold text-white">
                    <FiHeadphones className="h-5 w-5 text-[#A78BFA]" />
                    Audio
                  </button>
                  <button className="inline-flex items-center justify-center gap-2 rounded-lg bg-white/[0.06] px-3 py-3 text-sm font-extrabold text-white">
                    <FiVideo className="h-5 w-5 text-[#22D3EE]" />
                    Video
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 border-t border-white/10 pt-8 md:grid-cols-3">
            {reasons.map(({ icon: Icon, title, text }) => (
              <article key={title} className="rounded-lg border border-[#7C3AED]/35 bg-[#0A0820]/75 p-5">
                <Icon className="h-7 w-7 text-[#A78BFA]" />
                <h2 className="mt-4 text-lg font-extrabold tracking-normal text-white">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-[#D9D7FF]">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 px-5 py-12">
        <div className="mx-auto grid w-full max-w-6xl gap-8 lg:grid-cols-[0.72fr_0.28fr]">
          <div>
            <h2 className="max-w-2xl text-3xl font-black leading-tight tracking-normal text-white sm:text-4xl">
              One quiet place for the ways people actually talk.
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-8 text-[#D9D7FF]">
              Whizpr is a web-first messaging app for people who want direct conversations, not social noise. Start with text, send a voice message when tone matters, or move into an audio or video call from the same private space.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {capabilities.map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-lg border border-[#7C3AED]/35 bg-[#0A0820]/75 p-4">
                <Icon className="h-6 w-6 text-[#22D3EE]" />
                <p className="mt-3 text-sm font-extrabold leading-5 text-white">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default LandingPage;
